import { apiClient } from './client';
import type { ApiResponse } from '@/types/api.types';
import type {
  AuthUser,
  LoginRequest,
  LoginResponse,
  SelectContextRequest,
  RefreshTokenRequest,
  ContextCompany,
} from '@/types/auth.types';

export const authApi = {
  /**
   * POST /api/v1/auth/companies — step 1 of login (ENH-002).
   * Which companies can these credentials sign into? Empty means the credentials are wrong OR
   * the username is unknown — the two are deliberately indistinguishable.
   */
  companiesForCredentials: (data: { username: string; password: string }) =>
    apiClient.post<ApiResponse<ContextCompany[]>>('/auth/companies', data),

  /** POST /api/v1/auth/login — returns access token; refresh token set as HttpOnly cookie */
  login: (data: LoginRequest) =>
    apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data),

  /**
   * POST /api/v1/auth/refresh — rotates the refresh token (cookie) and returns
   * a new access token. The refresh token travels automatically as the
   * HttpOnly myledger_rt cookie — no token is sent in the request body.
   * The optional accessToken body field lets the server re-embed context claims.
   */
  refresh: (data?: RefreshTokenRequest) =>
    apiClient.post<ApiResponse<LoginResponse>>('/auth/refresh', data ?? {}),

  /**
   * POST /api/v1/auth/logout — revokes the refresh token server-side and clears
   * the HttpOnly cookie. Always call on sign-out.
   */
  logout: () =>
    apiClient.post<ApiResponse<null>>('/auth/logout'),

  /** POST /api/v1/auth/select-context — get enriched JWT after context selection */
  selectContext: (data: SelectContextRequest) =>
    apiClient.post<ApiResponse<LoginResponse>>('/auth/select-context', data),

  /** GET /api/v1/auth/me — current user profile (requires valid access token) */
  me: () => apiClient.get<ApiResponse<AuthUser>>('/auth/me'),

  /** GET /api/v1/auth/accessible-companies — live company list for the current user.
   *  Use this to refresh the context-switcher picker without rotating tokens. */
  accessibleCompanies: () =>
    apiClient.get<ApiResponse<ContextCompany[]>>('/auth/accessible-companies'),

  /** POST /api/v1/auth/change-password — self-service password change (requires current password) */
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    apiClient.post<ApiResponse<object>>('/auth/change-password', data),

  /** POST /api/v1/auth/forgot-password — anonymous */
  forgotPassword: (email: string) =>
    apiClient.post<ApiResponse<string>>('/auth/forgot-password', { email }),

  /** POST /api/v1/auth/reset-password-by-token — anonymous */
  resetPasswordByToken: (email: string, token: string, newPassword: string) =>
    apiClient.post<ApiResponse<string>>('/auth/reset-password-by-token', { email, token, newPassword }),
};
